import dayjs from 'dayjs';

import { Box, Typography } from '@mui/material';

import { ContractEntry, ContractSummary } from '@octopus/api';
import { getName, getRoleName, getWorkerId } from '@octopus/contract-types';
import { YyyyMmDd } from '@octopus/termination-api';

import UserAvatar from '../../../../modules/components/UserAvatar';
import { useGetEmployeeInfo } from '../../hooks/useGetEmployeeInfo';
import { Aggrements } from '../Aggrements';
import { EmployeeDetailsSkeleton } from '../Skeleton';
import { Vacations } from '../Vacations';

export const EmployeeInfo = ({
  selectedContracts,
  terminationDate,
}: {
  selectedContracts: ContractSummary[];
  terminationDate: YyyyMmDd;
}) => {
  const { contract, contractAccrualPeriods, isLoading } = useGetEmployeeInfo(
    selectedContracts?.[0],
  );

  if (isLoading) {
    return <EmployeeDetailsSkeleton />;
  }

  return (
    <Box
      component={'section'}
      data-test-id="container"
      p={3}
      mt={12}
      display="flex"
      flexDirection="column"
      gap="22px"
      border="1px solid #EDEDED"
      borderRadius="8px"
      bgcolor="#FFF"
    >
      <Header contract={contract.data} terminationDate={terminationDate} />
      <Aggrements contract={contract.data} />
      <Vacations vacations={contractAccrualPeriods.data} />
    </Box>
  );
};

const Header = ({
  contract,
  terminationDate,
}: {
  contract: ContractEntry;
  terminationDate: YyyyMmDd;
}) => {
  const employee = [
    getRoleName(contract),
    `Matrícula ${getWorkerId(contract)}`,
  ].filter(Boolean);

  const formatDate = (date: YyyyMmDd) => {
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch (error) {
      return date;
    }
  };

  return (
    <Box
      component="header"
      mb={4}
      display="flex"
      flexDirection="column"
      gap="12px"
    >
      <Box
        p={1.5}
        display="flex"
        alignItems="center"
        borderRadius="8px"
        bgcolor="#F7F7F8"
        gap="8px"
      >
        <UserAvatar
          avatarProps={{
            ml: 0,
            mr: 0,
            my: 0,
          }}
          name={getName(contract)}
          pictureUrl={contract.profilePicture?.pictureUrl}
          showFullName={false}
        />

        <Box>
          <Typography
            variant="body2"
            sx={{
              display: 'flex',
            }}
          >
            {getName(contract)}
          </Typography>
          {employee.map((detail, i) => (
            <Typography
              key={detail}
              variant="caption"
              color="#616161"
              sx={{
                display: 'inline-flex',
              }}
            >
              {detail}
              {i === 0 && employee.length > 1 && (
                <Typography
                  component="span"
                  variant="caption"
                  color="#BABABF"
                  sx={{
                    marginX: '4px',
                  }}
                >
                  |
                </Typography>
              )}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" gap={0.5}>
        <Typography fontSize={12} variant="subtitle2" color="text.secondary">
          Data efetiva do desligamento
        </Typography>
        <Typography fontSize={12} variant="subtitle2" color="text.primary">
          {formatDate(terminationDate)}
        </Typography>
      </Box>
    </Box>
  );
};
